import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';
import ToastContext from '../contexts/ToastContext';
import { FiCalendar, FiMapPin } from 'react-icons/fi';

export default function PersonalStats() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useContext(ToastContext);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const userId = currentUser?.id || currentUser?._id;
      const [{ data: created, error: createdError }, { data: joined, error: joinedError }, { data: history, error: historyError }] = await Promise.all([
        supabase.from('events').select('sport_type').eq('organizer_id', userId),
        supabase.from('event_participants').select('event_id, events(sport_type)').eq('user_id', userId),
        supabase.from('match_history').select('sport, result, xp_earned').eq('user_id', userId)
      ]);
      if (createdError || joinedError || historyError) throw createdError || joinedError || historyError;
      const sports = {};
      (joined || []).forEach(item => { const sport = item.events?.sport_type; if (sport) sports[sport] = (sports[sport] || 0) + 1; });
      setStats({ totalEventsAttended: (joined || []).length, totalEventsCreated: (created || []).length, participationRate: created?.length ? Math.round((joined?.length || 0) / created.length * 100) : 0, favoriteSpots: sports, matchHistory: history || [] });
    } catch (err) {
      console.error(err);
      if (toast?.error) toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="container">Chargement des statistiques...</div>;
  if (!stats) return <div className="container">Erreur lors du chargement des statistiques</div>;

  const topSports = Object.entries(stats.favoriteSpots || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  return (
    <div className="container">
      <h1>Mes Statistiques</h1>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginTop: '2rem'
      }}>
        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
          borderRadius: '8px',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
            {stats.totalEventsAttended}
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.9 }}>
            Événements auxquels j'ai participé
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, var(--success), #059669)',
          borderRadius: '8px',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
            {stats.totalEventsCreated}
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.9 }}>
            Événements créés
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, var(--accent-2), #7c3aed)',
          borderRadius: '8px',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
            {stats.participationRate}%
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.9 }}>
            Taux de participation
          </div>
        </div>
      </div>

      {topSports.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <h2>Sports Favoris</h2>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            {topSports.map(([sport, count]) => (
              <div key={sport} style={{
                padding: '1rem',
                background: 'var(--bg)',
                borderRadius: '6px',
                border: '2px solid var(--accent)',
                textAlign: 'center',
                minWidth: '120px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                  {count}
                </div>
                <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  {sport}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.upcomingEvents && stats.upcomingEvents.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <h2>Mes Événements à Venir</h2>
          <div style={{ marginTop: '1rem' }}>
            {stats.upcomingEvents.map(event => (
              <div key={event.id} style={{
                padding: '1rem',
                marginBottom: '0.5rem',
                background: 'var(--bg)',
                borderRadius: '6px',
                borderLeft: '4px solid var(--accent)'
              }}>
                <div style={{ fontWeight: 'bold' }}>
                  {event.sportType}
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FiMapPin size={16} /> {event.location}
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FiCalendar size={16} /> {new Date(event.date).toLocaleDateString('fr-FR')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.recentActivity && stats.recentActivity.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <h2>Activité Récente</h2>
          <div style={{ marginTop: '1rem' }}>
            {stats.recentActivity.map((activity, idx) => (
              <div key={idx} style={{
                padding: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                borderBottom: idx < stats.recentActivity.length - 1 ? '1px solid var(--border)' : 'none'
              }}>
                <span style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: 'var(--accent)'
                }} />
                <div>
                  <div style={{ fontWeight: 'bold' }}>
                    Participé à {activity.title}
                  </div>
                  <small style={{ color: 'var(--text-secondary)' }}>
                    {new Date(activity.date).toLocaleDateString('fr-FR')}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={fetchStats} className="btn btn-primary" style={{ marginTop: '2rem' }}>
        Rafraîchir les statistiques
      </button>
    </div>
  );
}
